import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useLocation } from "react-router-dom";
import { Film, Loader2, Play, X } from "lucide-react";
import { api } from "../lib/api";
import { useWatchModal } from "./WatchModalContext";

const TrailerModalContext = createContext({ openTrailer: () => {} });

const useTrailer = (item) => {
  const [state, setState] = useState({ status: "loading", key: null });
  useEffect(() => {
    const base = item.media_type === "tv" ? "/tv" : "/movies";
    api
      .get(`${base}/${item.id}/trailer`)
      .then((r) => setState({ status: "ready", key: r.data.key }))
      .catch(() => setState({ status: "none", key: null }));
  }, [item]);
  return state;
};

const TrailerBody = ({ item }) => {
  const { status, key } = useTrailer(item);
  if (status === "loading")
    return (
      <div data-testid="trailer-loading" className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  if (status === "none")
    return (
      <div data-testid="trailer-not-available" className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
        <Film className="h-10 w-10 text-[#D4AF37]" />
        <p className="font-display text-xl text-white">Trailer not available for this title</p>
        <p className="text-sm text-neutral-400">You can still head straight to the full feature.</p>
      </div>
    );
  return (
    <iframe
      data-testid="trailer-iframe"
      src={`https://www.youtube-nocookie.com/embed/${key}?autoplay=1&rel=0`}
      title={`${item.title} trailer`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      className="h-full w-full"
    />
  );
};

const TrailerModal = ({ item, open, onOpenChange }) => {
  const { openWatch } = useWatchModal();
  if (!item) return null;
  const close = () => onOpenChange(false);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay data-testid="trailer-modal-overlay" onClick={close} className="watch-overlay fixed inset-0 z-[60] bg-black/85 backdrop-blur-md" />
        <DialogPrimitive.Content
          data-testid="trailer-modal"
          aria-describedby={undefined}
          className="watch-content fixed left-1/2 top-1/2 z-[61] max-h-[94vh] w-[calc(100%-1.5rem)] max-w-5xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl border border-[#D4AF37]/25 bg-[#0d0d0d] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.95)] focus:outline-none"
        >
          <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-7">
            <div className="min-w-0">
              <p className="eyebrow text-[#D4AF37]">Official Trailer</p>
              <DialogPrimitive.Title data-testid="trailer-modal-title" className="mt-1 line-clamp-1 font-display text-xl font-semibold text-white sm:text-2xl">
                {item.title}
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close
              data-testid="trailer-modal-close"
              aria-label="Close trailer"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 bg-black/50 text-neutral-300 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="aspect-video w-full bg-black">{open && <TrailerBody item={item} />}</div>
          <div className="flex flex-col items-start justify-between gap-4 px-5 py-5 sm:flex-row sm:items-center sm:px-7">
            <p className="text-sm text-neutral-400">Liked what you saw? Stream the full {item.media_type === "tv" ? "series" : "movie"} now.</p>
            <button
              type="button"
              data-testid="trailer-modal-watch-now"
              onClick={() => {
                close();
                openWatch(item);
              }}
              className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-[0.14em]"
            >
              <Play className="h-4 w-4 fill-current" /> Watch Now
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export const TrailerModalProvider = ({ children }) => {
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  const openTrailer = useCallback((it) => {
    setItem(it);
    setSession((s) => s + 1);
    setOpen(true);
  }, []);
  return (
    <TrailerModalContext.Provider value={{ openTrailer }}>
      {children}
      <TrailerModal key={session} item={item} open={open} onOpenChange={setOpen} />
    </TrailerModalContext.Provider>
  );
};

export const useTrailerModal = () => useContext(TrailerModalContext);
